import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import {
  Blob1,
  Blob2,
  Blob3,
  Blob4,
  Container,
  OrbTheme,
  OrbWrap,
} from './VoiceOrb.styled';

export type VoiceOrbProps = {
  mediaStream?: MediaStream;
  autoStart?: boolean;
  className?: string;
  variant?: 'cedar' | 'marin';
  isSpeaking?: boolean;
};

export type VoiceOrbHandle = {
  start: () => Promise<void>;
  stop: () => void;
};

const VoiceOrb = forwardRef<VoiceOrbHandle, VoiceOrbProps>(
  (
    {
      mediaStream,
      autoStart = false,
      className,
      variant = 'cedar',
      isSpeaking = false,
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const dataArrayRef = useRef<Float32Array | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const rafIdRef = useRef<number | null>(null);
    const displayedLevelRef = useRef(0);
    const speakingTargetRef = useRef(0);
    const smoothedSpeakingRef = useRef(0);
    const smoothedRmsRef = useRef(0);
    const isSpeakingRef = useRef(isSpeaking);
    const [listening, setListening] = useState(false);

    const setScaleFromLevel = useCallback((level: number) => {
      if (!containerRef.current) {
        return;
      }
      const minScale = 1.0;
      const maxScale = 1.18;
      const scaled = Math.min(
        maxScale,
        Math.max(minScale, minScale + level * 0.8),
      );
      containerRef.current.style.setProperty('--orb-scale', scaled.toFixed(3));
    }, []);

    const computeLevel = useCallback(() => {
      if (!analyserRef.current || !dataArrayRef.current) {
        return 0;
      }
      (analyserRef.current as any).getFloatTimeDomainData(
        dataArrayRef.current as any,
      );
      let sumSquares = 0;
      for (let i = 0; i < dataArrayRef.current.length; i++) {
        const v = dataArrayRef.current[i];
        sumSquares += v * v;
      }
      const rms = Math.sqrt(sumSquares / dataArrayRef.current.length);
      return rms;
    }, []);

    const animate = useCallback(() => {
      const measuredRms = computeLevel();
      // Smooth rms to avoid jitter
      smoothedRmsRef.current += (measuredRms - smoothedRmsRef.current) * 0.25;
      const desiredSpeakingLevel = isSpeakingRef.current ? 0.2 : 0;
      smoothedSpeakingRef.current +=
        (desiredSpeakingLevel - smoothedSpeakingRef.current) * 0.08;
      const target = Math.max(
        smoothedRmsRef.current,
        smoothedSpeakingRef.current,
      );
      const current = displayedLevelRef.current;
      const easing = target > current ? 0.08 : 0.06;
      const delta = target - current;
      const maxUpStep = 0.008;
      const step =
        delta > 0 ? Math.min(delta * easing, maxUpStep) : delta * easing;
      const next = current + step;
      displayedLevelRef.current = next;
      setScaleFromLevel(next);

      if (containerRef.current) {
        const intensity = Math.min(1, next * 3); // map small RMS to [0..1]
        const maxOffset = 10; // px
        // add slight phase wobble based on time for organic feel
        const t = performance.now() / 1000;
        const dx =
          (Math.sin(t * 3.1) + Math.sin(t * 1.7 + 1.2)) *
          0.5 *
          intensity *
          maxOffset;
        const dy =
          (Math.cos(t * 2.6) + Math.cos(t * 2.2 + 0.7)) *
          0.5 *
          intensity *
          maxOffset;
        containerRef.current.style.setProperty(
          '--orb-dx',
          `${dx.toFixed(2)}px`,
        );
        containerRef.current.style.setProperty(
          '--orb-dy',
          `${dy.toFixed(2)}px`,
        );
      }
      rafIdRef.current = requestAnimationFrame(animate);
    }, [computeLevel, setScaleFromLevel]);

    const start = useCallback(async () => {
      try {
        if (!audioContextRef.current) {
          audioContextRef.current = new (
            window.AudioContext || (window as any).webkitAudioContext
          )();
        }
        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
        }

        const stream =
          mediaStream ||
          (await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
          }));

        streamRef.current = stream;

        const source = audioContextRef.current.createMediaStreamSource(stream);
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 2048;
        analyserRef.current.smoothingTimeConstant = 0.9;
        dataArrayRef.current = new Float32Array(analyserRef.current.fftSize);
        source.connect(analyserRef.current);

        setListening(true);
        if (!rafIdRef.current) {
          animate();
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    }, [mediaStream, animate]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: existing deps are intentional
    const stop = useCallback(() => {
      analyserRef.current = null;
      dataArrayRef.current = null;
      if (streamRef.current && !mediaStream) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
      streamRef.current = null;
      setListening(false);
      if (containerRef.current) {
        containerRef.current.style.setProperty('--orb-dx', '0px');
        containerRef.current.style.setProperty('--orb-dy', '0px');
      }
    }, [mediaStream, setScaleFromLevel]);

    useImperativeHandle(ref, () => ({ start, stop }), [start, stop]);

    useEffect(() => {
      setScaleFromLevel(0);
      if (containerRef.current) {
        containerRef.current.style.setProperty('--orb-scale', '1');
      }
    }, [setScaleFromLevel]);

    useEffect(() => {
      speakingTargetRef.current = isSpeaking ? 0.2 : 0;
      isSpeakingRef.current = isSpeaking;
    }, [isSpeaking]);

    useEffect(() => {
      if (!rafIdRef.current) {
        rafIdRef.current = requestAnimationFrame(animate);
      }
      return () => {
        if (rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current);
        }
        rafIdRef.current = null;
        if (containerRef.current) {
          containerRef.current.style.setProperty('--orb-dx', '0px');
          containerRef.current.style.setProperty('--orb-dy', '0px');
          containerRef.current.style.setProperty('--orb-scale', '1');
        }
      };
    }, [animate]);

    useEffect(() => {
      if (autoStart && !listening) {
        void start();
      }
    }, [autoStart, listening, start]);

    return (
      <Container className={className}>
        <OrbTheme $variant={variant}>
          <OrbWrap
            ref={containerRef}
            aria-label={
              isSpeaking ? 'Speaking' : listening ? 'Listening' : 'Idle'
            }
          >
            <Blob1 />
            <Blob2 />
            <Blob3 />
            <Blob4 />
          </OrbWrap>
        </OrbTheme>
      </Container>
    );
  },
);

export default VoiceOrb;
